<phms-workflow-dashboard-navigation />
<ui-section>
  <ui-block>
    <ui-panel
      width="fill"
      class="platform-access-admin"
      panelTitle="Platform Navigation"
      title="Platform Navigation"
      [subtitle]="pagePanelSubtitle"
      [panelHeaderConfig]="pagePanelHeaderConfig"
      (buttonClick)="onPagePanelHeaderButtonClick($event)"
    >
      <div class="message-box success" *ngIf="successMessage()" role="status" aria-live="polite">
        {{ successMessage() }}
      </div>

      <div class="message-box error" *ngIf="errorState() as error" role="alert" aria-live="assertive">
        <p>{{ error.title }}</p>
        <ul>
          <li *ngFor="let message of error.messages">{{ message }}</li>
        </ul>

        <div class="error-meta" *ngIf="error.problemCode || error.issueCodes.length > 0 || error.correlationId">
          <span *ngIf="error.problemCode">Problem code: {{ error.problemCode }}</span>
          <span *ngIf="error.issueCodes.length > 0">Issue codes: {{ error.issueCodes.join(', ') }}</span>
          <span *ngIf="error.correlationId">Correlation ID: {{ error.correlationId }}</span>
        </div>
      </div>

      <div class="access-shell">
        <ui-grid
          class="catalog-panel"
          [dsConfig]="catalogGridConfig"
          [panelHeaderConfig]="catalogPanelHeaderConfig"
          [showPanelHeader]="true"
          [reloadOnChangeTrigger]="catalogReloadTrigger()"
        ></ui-grid>

        <ui-panel
          class="detail-panel-host"
          [panelTitle]="isCreatingNavigationNode() ? 'Create Navigation Node' : (selectedNavigationNode()?.displayName || 'Navigation Detail')"
          [title]="isCreatingNavigationNode() ? 'Create Navigation Node' : (selectedNavigationNode()?.displayName || 'Navigation Detail')"
        >
          <div class="detail-panel__body" aria-label="Navigation detail">
            <p class="loading-text" *ngIf="isNavigationLoading()" role="status" aria-live="polite">
              Loading the selected navigation node...
            </p>

            <ng-container *ngIf="!isNavigationLoading()">
              <!-- Nothing selected and not creating: guide the user instead of showing a dead form. -->
              <div class="detail-empty-state" *ngIf="isNavigationDetailEmpty()">
                <span class="detail-empty-state__glyph" aria-hidden="true">&#9776;</span>
                <h4>No navigation node selected</h4>
                <p>Select a node from the catalog to view and edit it, or create a new one.</p>
                <ui-button
                  [buttonText]="'New Node'"
                  [disabled]="!canAuthor() || isNavigationSaving()"
                  (click)="beginCreateNavigationNode()"
                ></ui-button>
              </div>

              <ng-container *ngIf="!isNavigationDetailEmpty()">
                <div class="detail-panel__header" *ngIf="selectedNavigationNode() as node">
                  <div class="detail-panel__status">
                    <span class="status-pill">{{ selectedNavigationStatusLabel }}</span>
                    <span>Type: {{ node.nodeTypeCode }}</span>
                    <span>Sort: {{ node.sortOrder }}</span>
                  </div>
                </div>

                <div class="inline-note" *ngIf="navigationEditBlockedReason()">
                  {{ navigationEditBlockedReason() }}
                </div>

                <ui-form
                  #navigationNodeForm
                  [config]="navigationNodeFormConfig"
                  [(formValues)]="navigationNodeFormValues"
                  [disabled]="isNavigationSaving() || !!navigationEditBlockedReason()"
                  [showFormErrors]="true"
                ></ui-form>

                <!-- Action bar: edit actions (Save / Reset) left, lifecycle transitions right. -->
                <div class="detail-actions">
                  <ui-button-group
                    class="detail-actions__edit"
                    [buttonsConfig]="navigationNodeEditButtons()"
                    (buttonClick)="onNavigationNodeFormActionButtonClick($event)"
                  ></ui-button-group>

                  <ui-button-group
                    class="detail-actions__lifecycle"
                    [buttonsConfig]="navigationNodeLifecycleButtons()"
                    (buttonClick)="onNavigationNodeFormActionButtonClick($event)"
                  ></ui-button-group>
                </div>

              <div class="rules-section">
                <div class="rules-section__header">
                  <div>
                    <h4>Visibility Rules</h4>
                    <p>
                      Rules are staged locally until the navigation node is saved. Use stable permission or feature-flag codes.
                    </p>
                  </div>

                  <ui-button
                    [buttonText]="'New Rule'"
                    [disabled]="!canAuthor() || !!navigationRuleBlockedReason()"
                    (click)="startCreateRule()"
                  ></ui-button>
                </div>

                <div class="inline-note" *ngIf="navigationRuleBlockedReason()">
                  {{ navigationRuleBlockedReason() }}
                </div>

                <p class="empty-state" *ngIf="sortedNavigationRules().length === 0">
                  No visibility rules have been staged for this node yet.
                </p>

                <div class="rule-grid" *ngIf="sortedNavigationRules().length > 0">
                  <article class="lifecycle-card" *ngFor="let rule of sortedNavigationRules(); let i = index">
                    <div class="lifecycle-card__header">
                      <div>
                        <h5>{{ toStatusLabel(rule.ruleTypeCode) }}</h5>
                        <span class="lifecycle-card__code">{{ formatRuleTarget(rule) }}</span>
                      </div>

                      <div class="lifecycle-card__actions">
                        <span class="status-pill">{{ toStatusLabel(rule.visibilityModeCode) }}</span>
                        <ui-button
                          severity="secondary"
                          [buttonText]="'Edit'"
                          [disabled]="!canAuthor() || !!navigationRuleBlockedReason()"
                          (click)="editRule(i)"
                        ></ui-button>
                        <ui-button
                          severity="danger"
                          [buttonText]="'Remove'"
                          [disabled]="!canAuthor() || !!navigationRuleBlockedReason()"
                          (click)="removeRule(i)"
                        ></ui-button>
                      </div>
                    </div>

                    <dl class="lifecycle-card__details">
                      <div>
                        <dt>Sort Order</dt>
                        <dd>{{ rule.sortOrder }}</dd>
                      </div>
                      <div>
                        <dt>Lifecycle</dt>
                        <dd>{{ formatLifecycleWindow(rule.effectiveStartUtc, rule.effectiveEndUtc) }}</dd>
                      </div>
                    </dl>
                  </article>
                </div>

                <div class="rule-editor" *ngIf="isRuleEditorVisible()">
                  <div class="detail-card__header">
                    <div>
                      <h4>{{ editingRuleIndex() === null ? 'Stage Visibility Rule' : 'Edit Visibility Rule' }}</h4>
                      <p>Save the navigation node after staging rules so the backend can validate and persist them.</p>
                    </div>
                  </div>

                  <ui-form
                    #navigationRuleForm
                    [config]="navigationRuleFormConfig"
                    [(formValues)]="navigationRuleFormValues"
                    [disabled]="isNavigationSaving()"
                    [showFormErrors]="true"
                  ></ui-form>

                  <ui-button-group
                    class="form-actions"
                    [buttonsConfig]="navigationRuleFormActionButtons()"
                    (buttonClick)="onNavigationRuleFormActionButtonClick($event)"
                  ></ui-button-group>
                </div>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </ui-panel>
      </div>
    </ui-panel>
  </ui-block>
</ui-section>
